import React, { useRef, useState, useCallback, useEffect } from 'react';
import { RotateCcw, Route, Eye, EyeOff } from 'lucide-react';

/**
 * SceneInteractiveSVG — Composant réutilisable pour les scènes SVG interactives
 * "table de sable ECSR" : vue de dessus avec voitures/pièces déplaçables au drag & drop,
 * bouton pour afficher/masquer la trajectoire de correction, bouton reset.
 *
 * Le SVG passé en `children` doit :
 *  - avoir un attribut `viewBox` (obligatoire pour le mapping des coordonnées)
 *  - déclarer les éléments déplaçables avec la classe `.cv-drag` (voitures, pièces mobiles)
 *  - déclarer les éléments "correction" (trajet, zones) avec la classe `.cv-route` ou `.cv-zone`
 *    → ces éléments seront révélés quand `showRoute` est vrai (via la classe `cv-show-route`)
 *
 * Props :
 *  - `title` (string, optionnel) — titre affiché au-dessus de la scène
 *  - `hint` (string, optionnel) — indication pédagogique sous la scène
 *  - `toggleLabel` (string) — texte du bouton toggle correction (défaut "Afficher trajet")
 *  - `testid` (string, optionnel) — data-testid préfixe
 *  - `children` — la balise <svg> avec ses contenus
 */
export default function SceneInteractiveSVG({ title, hint, toggleLabel = 'Afficher trajet', testid, children }) {
  const wrapRef = useRef(null);
  const [showRoute, setShowRoute] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  // Attache les handlers pointer sur tous les .cv-drag une fois le SVG monté.
  // Utilise resetKey pour re-attacher après un reset (les transforms sont restaurés).
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const svg = wrap.querySelector('svg[data-scene], svg.cv-inline-svg');
    if (!svg) return;

    // Sauvegarder les transforms d'origine (première fois seulement)
    const draggables = Array.from(svg.querySelectorAll('.cv-drag, .drag'));
    const originals = draggables.map((el) => [el, el.getAttribute('transform') || '']);

    const handlers = [];
    draggables.forEach((el) => {
      let start = null;
      let origin = { x: 0, y: 0 };

      const pointerAt = (evt) => {
        const pt = svg.createSVGPoint();
        pt.x = evt.clientX;
        pt.y = evt.clientY;
        return pt.matrixTransform(svg.getScreenCTM().inverse());
      };

      const onDown = (evt) => {
        evt.preventDefault();
        el.setPointerCapture(evt.pointerId);
        el.classList.add('cv-selected');
        const m = (el.transform.baseVal.consolidate() || { matrix: { e: 0, f: 0 } }).matrix;
        origin = { x: m.e, y: m.f };
        start = pointerAt(evt);
      };
      const onMove = (evt) => {
        if (!start) return;
        const now = pointerAt(evt);
        const dx = now.x - start.x;
        const dy = now.y - start.y;
        const oldTr = el.getAttribute('transform') || '';
        const rot = (oldTr.match(/rotate\([^)]*\)/) || [''])[0];
        el.setAttribute('transform', `translate(${origin.x + dx} ${origin.y + dy}) ${rot}`);
      };
      const onUp = () => {
        start = null;
        el.classList.remove('cv-selected');
      };

      el.addEventListener('pointerdown', onDown);
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerup', onUp);
      el.addEventListener('pointercancel', onUp);
      handlers.push({ el, onDown, onMove, onUp });
    });

    // Reset callback : restaure les transforms originaux
    const resetFn = () => {
      originals.forEach(([el, tr]) => {
        if (tr) el.setAttribute('transform', tr);
        else el.removeAttribute('transform');
      });
    };
    wrap._resetFn = resetFn;

    return () => {
      handlers.forEach(({ el, onDown, onMove, onUp }) => {
        el.removeEventListener('pointerdown', onDown);
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerup', onUp);
        el.removeEventListener('pointercancel', onUp);
      });
    };
  }, [resetKey]);

  const handleReset = useCallback(() => {
    const wrap = wrapRef.current;
    if (wrap && wrap._resetFn) wrap._resetFn();
    setShowRoute(false);
    setResetKey((k) => k + 1);
  }, []);

  return (
    <div className="rounded-xl border border-white/10 bg-black/40 overflow-hidden" data-testid={testid}>
      {title && (
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-serif-editorial text-white text-[17px] leading-tight">{title}</div>
        </div>
      )}
      <div
        ref={wrapRef}
        className={`cv-scene-wrap p-2 sm:p-3 ${showRoute ? 'cv-show-route' : ''}`}
      >
        {children}
      </div>
      <div className="px-4 py-3 border-t border-white/10 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setShowRoute((s) => !s)}
          data-testid={testid ? `${testid}-toggle` : undefined}
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[12.5px] font-bold transition"
        >
          {showRoute ? <><EyeOff size={13} /> Masquer correction</> : <><Route size={13} /> {toggleLabel}</>}
        </button>
        <button
          type="button"
          onClick={handleReset}
          data-testid={testid ? `${testid}-reset` : undefined}
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-white/20 text-white/80 hover:bg-white/5 text-[12.5px] font-semibold transition"
        >
          <RotateCcw size={13} /> Reset
        </button>
        <span className="ml-auto text-white/40 text-[11px] font-mono-spec">Fais glisser les véhicules</span>
      </div>
      {hint && (
        <div className="px-4 pb-4 text-white/60 text-[12.5px] leading-relaxed">{hint}</div>
      )}
    </div>
  );
}
